import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { hasPremiumAccess } from '../lib/subscriptionAccess';
import { notify } from '../store/notificationStore';

export function GracePeriodNotifier() {
  const { user } = useAuthStore();
  const subscription = user?.subscription;

  useEffect(() => {
    if (!subscription) return;

    // Skip notifications for active paid/premium subscribers
    if (hasPremiumAccess(subscription)) return;

    // Check if user is grandfathered
    const isGrandfathered = subscription.is_grandfathered === true;

    if (isGrandfathered && subscription.days_until_grandfathered_expires !== undefined) {
      const daysUntilExpiry = subscription.days_until_grandfathered_expires;

      // Check if we've already notified today
      const today = new Date();
      const notificationKey = `grace-period-notified-${today.toISOString().split('T')[0]}`;
      const hasNotifiedToday = localStorage.getItem(notificationKey);

      // Check if this is a new user who just applied a promo code
      const isNewUser = localStorage.getItem('invitation-code-just-applied');

      if (!hasNotifiedToday) {
        // If a promo code was just applied, show success message
        if (isNewUser) {
          notify.success(
            'Promo Code Applied Successfully!',
            `You now have premium access for ${daysUntilExpiry} days. Add a payment method anytime to continue after the trial.`
          );
          localStorage.removeItem('invitation-code-just-applied');
        } else {
          // Show notifications based on days remaining
          if (daysUntilExpiry <= 0) {
            notify.error(
              'Premium Access Expired',
              'Your premium access has expired. Add a payment method to continue using premium features.'
            );
          } else if (daysUntilExpiry <= 3) {
            notify.warning(
              `${daysUntilExpiry} Days Remaining`,
              'Your premium access expires very soon. Add a payment method to avoid interruption.'
            );
          } else if (daysUntilExpiry <= 7) {
            notify.warning(
              `${daysUntilExpiry} Days Until Expiry`,
              'Your premium trial is ending soon. Add a payment method to continue.'
            );
          } else if (daysUntilExpiry <= 14) {
            notify.info(
              'Grace Period Active',
              `You have ${daysUntilExpiry} days to add a payment method for uninterrupted premium access.`
            );
          }
        }

        localStorage.setItem(notificationKey, 'true');

        // Clean up old notification keys
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('grace-period-notified-') && key !== notificationKey) {
            localStorage.removeItem(key);
          }
        });
      }
    }
  }, [subscription]);

  // This component doesn't render anything
  return null;
}
